import PrimaryButton from '@/components/Buttons/PrimaryButton';
import PageTitle from '@/components/PageTitle';
import { Heading, Text } from '@/components/Typography';
import AuthLayout from '@/layouts/AuthLayout';
import { Link, useForm, usePage } from '@inertiajs/react';
import clsx from 'clsx';
import { Eye, EyeOff } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

type PageProps = {
    shared: {
        messages: { level: string; message: string; tags: string }[];
    };
};

const Login = () => {
    const {
        shared: { messages },
    } = usePage<PageProps>().props;

    const { t } = useTranslation();

    const [showPassword, setShowPassword] = useState(false);

    const { data, setData, post, errors, processing } = useForm<{
        email: string;
        password: string;
        remember: boolean;
    }>({
        email: '',
        password: '',
        remember: false,
    });

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        post('/auth/login/');
    };

    return (
        <>
            <PageTitle title="Iniciar Sesion" />
            <AuthLayout>
                <div className="w-full">
                    <div className="mb-6 text-center">
                        <Heading
                            as="h1"
                            className="mb-3 text-2xl font-semibold"
                        >
                            {t('auth.login.title')}
                        </Heading>
                        <Text size="sm" muted>
                            {t('auth.login.description')}
                        </Text>
                    </div>

                    <form onSubmit={onSubmit}>
                        <div className="flex flex-col gap-y-5">
                            <div>
                                <label
                                    htmlFor="email"
                                    className="mb-1.5 block text-sm font-medium text-gray-700"
                                >
                                    {t('auth.login.email')}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={data.email}
                                    onChange={(e) =>
                                        setData('email', e.target.value)
                                    }
                                    placeholder={t('home.emailPlaceholder')}
                                    className={clsx(
                                        'w-full rounded-md border px-3 py-2 text-sm text-black shadow-sm transition-colors focus:ring-2 focus:outline-none',
                                        errors.email
                                            ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                                            : 'border-gray-300 focus:border-gray-400 focus:ring-gray-200'
                                    )}
                                />
                                {errors.email && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.email}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="password"
                                    className="mb-1.5 block text-sm font-medium text-gray-700"
                                >
                                    {t('auth.login.password')}
                                </label>
                                <div className="relative">
                                    <input
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        id="password"
                                        name="password"
                                        value={data.password}
                                        onChange={(e) =>
                                            setData('password', e.target.value)
                                        }
                                        placeholder="••••••••"
                                        className={clsx(
                                            'w-full rounded-md border py-2 pr-10 pl-3 text-sm text-black shadow-sm transition-colors focus:ring-2 focus:outline-none',
                                            errors.password
                                                ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                                                : 'border-gray-300 focus:border-gray-400 focus:ring-gray-200'
                                        )}
                                    />
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowPassword((v) => !v)
                                        }
                                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                                        aria-label={
                                            showPassword
                                                ? 'Ocultar contraseña'
                                                : 'Mostrar contraseña'
                                        }
                                    >
                                        {showPassword ? (
                                            <EyeOff size={16} />
                                        ) : (
                                            <Eye size={16} />
                                        )}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.password}
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="remember"
                                    name="remember"
                                    checked={data.remember}
                                    onChange={(e) =>
                                        setData('remember', e.target.checked)
                                    }
                                    className="mr-2"
                                />
                                <label
                                    htmlFor="remember"
                                    className="text-sm text-gray-600"
                                >
                                    {t('auth.login.remember')}
                                </label>
                            </div>

                            {messages && messages.length > 0 && (
                                <div className="rounded border border-red-200 bg-red-50 p-2.5">
                                    {messages.map((message) => (
                                        <p
                                            key={message.message}
                                            className="text-xs text-red-600"
                                        >
                                            {message.message}
                                        </p>
                                    ))}
                                </div>
                            )}

                            <PrimaryButton
                                type="submit"
                                disabled={processing}
                                loading={processing}
                                size="full-width"
                            >
                                {processing
                                    ? t('auth.login.loading')
                                    : t('auth.login.login')}
                            </PrimaryButton>

                            <div className="pt-1 text-center">
                                <p className="inline-flex flex-wrap items-baseline justify-center gap-x-1 gap-y-1 text-sm text-gray-500">
                                    <span>
                                        {t(
                                            'auth.login.forgotPasswordDescription'
                                        )}
                                    </span>
                                    <Link
                                        href="/auth/forgot-password/"
                                        className="font-medium text-gray-900 hover:underline"
                                    >
                                        {t('auth.login.forgotPassword')}
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </form>
                </div>
            </AuthLayout>
        </>
    );
};

export default Login;
